import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Save, Trash2 } from "lucide-react";

const LANGS = [
  { code: "en-US", label: "English" },
  { code: "es-ES", label: "Español" },
  { code: "es-MX", label: "Español (MX)" },
  { code: "fr-FR", label: "Français" },
];

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const [lang, setLang] = useState(() => localStorage.getItem("ol_journal_lang") || "en-US");
  const recogRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const r = new SR();
    r.lang = lang;
    r.continuous = true;
    r.interimResults = false;
    recogRef.current = r;
    api.get("/journal").then(({ data }) => setEntries(data));
  }, []);

  useEffect(() => {
    if (recogRef.current) recogRef.current.lang = lang;
    localStorage.setItem("ol_journal_lang", lang);
  }, [lang]);

  const start = () => {
    if (!recogRef.current) return;
    const r = recogRef.current;
    r.onresult = (e) => {
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript + " ";
      }
      if (final) setText((t) => (t + " " + final).trim());
    };
    r.onerror = (ev) => { toast.error(`Mic: ${ev.error}`); setListening(false); };
    r.onend = () => setListening(false);
    try { r.start(); setListening(true); } catch {}
  };
  const stop = () => { try { recogRef.current?.stop(); } catch {}; setListening(false); };

  const save = async () => {
    if (!text.trim()) return toast.error("Nothing to save yet");
    try {
      const { data } = await api.post("/journal", { title: title || "Untitled", text });
      setEntries((prev) => [data, ...prev]);
      setTitle(""); setText("");
      toast.success("Saved to journal");
    } catch { toast.error("Could not save"); }
  };

  return (
    <div className="space-y-6" data-testid="journal-page">
      <div>
        <div className="chip mb-2">Journal · Voice-friendly</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Say what's on your mind.</h1>
        <p className="text-slate-600 mt-1 max-w-2xl">Tap the mic and talk. We'll turn your voice into text so writing never blocks your thinking.</p>
      </div>

      <div className="p-6 rounded-3xl bg-white border border-slate-200 space-y-3">
        <input data-testid="journal-title" value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="Title (optional)"
          className="w-full px-4 py-3 rounded-xl border border-slate-200" />
        <textarea data-testid="journal-text" value={text} onChange={(e)=>setText(e.target.value)}
          rows={8} placeholder="Start talking or typing…"
          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 outline-none leading-relaxed" />

        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="journal-lang" value={lang} onChange={(e)=>setLang(e.target.value)}
            className="px-3 py-2 rounded-full border border-slate-200 bg-white text-sm">
            {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
          {!listening ? (
            <button data-testid="journal-mic-start" onClick={start} disabled={!supported}
              className="px-5 py-2.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
              <Mic size={14} /> {supported ? "Start speaking" : "Voice not supported"}
            </button>
          ) : (
            <button data-testid="journal-mic-stop" onClick={stop}
              className="px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold flex items-center gap-2">
              <Square size={14} /> Stop
            </button>
          )}
          <button data-testid="journal-save" onClick={save}
            className="px-5 py-2.5 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold flex items-center gap-2">
            <Save size={14} /> Save entry
          </button>
          {!supported && <span className="text-xs text-slate-500">Try Chrome or Edge for voice.</span>}
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Your entries</h2>
        <div className="space-y-3" data-testid="journal-list">
          {entries.map((e) => (
            <div key={e.id} className="p-5 rounded-2xl bg-white border border-slate-200">
              <div className="font-display font-semibold text-slate-900">{e.title || "Untitled"}</div>
              <div className="text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</div>
              <div className="mt-2 whitespace-pre-wrap text-slate-800 text-sm">{e.text}</div>
            </div>
          ))}
          {entries.length === 0 && <div className="text-slate-500 text-sm">No entries yet.</div>}
        </div>
      </div>
    </div>
  );
}
